import { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";

/**
 * PreferenceCategoriesPanel — Manager+ dashboard panel for CRUD on the
 * categories that drive the Service module guest-preference dashboard. Seeded with 8
 * defaults on backend startup so this panel is only needed when the
 * yacht wants to add / rename / disable categories.
 */
export default function PreferenceCategoriesPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ id: null, name: "", color: "", sort_order: 0, active: true });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/preference-categories");
      setRows(data || []);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Could not load categories"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const startNew = () => { setForm({ id: null, name: "", color: "#6f8cad", sort_order: (rows.length + 1) * 10, active: true }); setOpen(true); };
  const startEdit = (r) => { setForm({ id: r.id, name: r.name, color: r.color || "", sort_order: r.sort_order || 0, active: r.active !== false }); setOpen(true); };

  const save = async () => {
    try {
      const payload = { name: form.name.trim(), color: form.color || null, sort_order: Number(form.sort_order || 0), active: !!form.active };
      if (form.id) await api.put(`/admin/preference-categories/${form.id}`, payload);
      else await api.post("/admin/preference-categories", payload);
      toast.success("Saved");
      setOpen(false);
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Could not save"); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete "${r.name}"? Existing guest entries under this category will remain in the database.`)) return;
    try { await api.delete(`/admin/preference-categories/${r.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Could not delete"); }
  };

  return (
    <div className="p-6" data-testid="preference-categories-panel">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="overline text-primary/60 mb-1">Service module</div>
          <h2 className="font-display text-2xl">Preference categories</h2>
          <p className="text-sm text-muted-foreground mt-1">Configure the reusable service-preference fields crew fill in for each guest. Regulars keep these values across trips.</p>
        </div>
        <Button onClick={startNew} data-testid="pref-cat-new"><Plus className="h-4 w-4 mr-2" /> New category</Button>
      </div>

      <div className="border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              <th className="p-3 text-left overline text-[10px]">Order</th>
              <th className="p-3 text-left overline text-[10px]">Name</th>
              <th className="p-3 text-left overline text-[10px]">Colour</th>
              <th className="p-3 text-left overline text-[10px]">Active</th>
              <th className="p-3 text-right overline text-[10px]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="p-6 text-center text-muted-foreground">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={5} className="p-6 text-center text-muted-foreground">No categories yet. Click "New category" to add one.</td></tr>
            ) : (
              rows.map((r) => (
                <tr key={r.id} className="border-t border-border" data-testid={`pref-cat-row-${r.id}`}>
                  <td className="p-3">{r.sort_order || 0}</td>
                  <td className="p-3 font-medium">{r.name}</td>
                  <td className="p-3">
                    {r.color && <span className="inline-flex items-center gap-2"><span className="inline-block w-4 h-4 border border-border" style={{ backgroundColor: r.color }} /><span className="text-xs">{r.color}</span></span>}
                  </td>
                  <td className="p-3">{r.active === false ? <span className="text-muted-foreground">Hidden</span> : <span className="text-emerald-700">Active</span>}</td>
                  <td className="p-3 text-right space-x-2">
                    <Button size="sm" variant="outline" onClick={() => startEdit(r)} data-testid={`pref-cat-edit-${r.id}`}><Pencil className="h-3 w-3" /></Button>
                    <Button size="sm" variant="outline" onClick={() => remove(r)} data-testid={`pref-cat-del-${r.id}`}><Trash2 className="h-3 w-3" /></Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="pref-cat-dialog">
          <DialogHeader><DialogTitle>{form.id ? "Edit category" : "New category"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Cold Drinks" data-testid="pref-cat-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Colour</Label>
                <input type="color" value={form.color || "#6f8cad"} onChange={(e) => setForm({ ...form, color: e.target.value })} className="mt-1 w-full h-10 border border-border" data-testid="pref-cat-color" />
              </div>
              <div>
                <Label>Sort order</Label>
                <Input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} data-testid="pref-cat-order" />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="pref-cat-active" />
              Active (uncheck to hide from crew without deleting)
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={!form.name.trim()} data-testid="pref-cat-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
